import React from 'react';
import TabBar from './TabBar';
import NavigationButtons from './NavigationButtons';
import SidebarPanelIcon from './SidebarPanelIcon';
import { BrainIcon, SettingsIcon } from '../Sidebar/icons';
import SidebarWorkspaceSwitcher from '../Sidebar/components/SidebarWorkspaceSwitcher';
import NotificationBell from './NotificationBell';
import './Navbar.css';

const FokusMark = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 382 402"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M264.682 99.3255C329.545 99.7438 382 153.418 382 218.417C382 279.377 335.862 330.375 276.621 336.823V278.693C303.682 272.706 324.606 248.048 324.606 218.417C324.606 184.286 296.845 156.752 263.919 156.752H57.3941V219.358C57.3941 248.703 78.1961 273.185 105.85 278.848V336.884C46.6306 330.785 0.386915 280.898 0.00229709 220.122L0 219.358V99.3232H263.919L264.682 99.3255Z" fill="currentColor"/>
    <path d="M219.697 402H162.773V199.117H219.697V402Z" fill="currentColor"/>
    <path d="M219.697 56.9578H162.773V0H219.697V56.9578Z" fill="currentColor"/>
  </svg>
);

/**
 * Navbar — tab strip over the main workspace.
 * Leading chrome (collapse, back/forward) sits on the black bar before the first tab.
 * Brain lives in the sidebar footer; it only appears here when the sidebar is collapsed.
 */
const Navbar = ({
  sprints = [],
  onTabClick,
  onNewTab,
  sidebarCollapsed = false,
  onToggleSidebar,
  onHistoryNavigate,
  brainOpen = false,
  onToggleBrain,
  onOpenSettings,
  workspaceId,
  workspaces,
  setWorkspace,
  suppressActiveTab = false,
}) => {
  const isElectron = window.electronAPI?.isElectron;

  const settingsButton = onOpenSettings ? (
    <button
      type="button"
      className="navbar__settings-btn"
      onClick={onOpenSettings}
      title="Settings"
      aria-label="Settings"
    >
      <SettingsIcon size={15} />
    </button>
  ) : null;

  return (
    <nav
      className={`navbar ${isElectron ? 'navbar--electron' : ''} ${
        sidebarCollapsed ? 'navbar--sidebar-collapsed' : ''
      }`}
    >
      {/* Settings sits flush left of the floating WindowControls.
          A separate no-drag zone covers the min/max/close footprint. */}
      {isElectron && <div className="navbar__controls-zone" aria-hidden="true" />}
      <div className={`navbar__trailing ${isElectron ? 'navbar__trailing--electron' : ''}`}>
        <NotificationBell />
        {settingsButton}
      </div>

      <div className="navbar__leading">
        {sidebarCollapsed && (
          <div className="navbar__identity">
            <a href="/" className="navbar__collapsed-brand" title="Fokus" aria-label="Fokus">
              <FokusMark className="navbar__collapsed-brand-icon" />
            </a>
            <SidebarWorkspaceSwitcher
              variant="navbar"
              workspaceId={workspaceId}
              workspaces={workspaces}
              setWorkspace={setWorkspace}
              onOpenSettings={onOpenSettings}
            />
          </div>
        )}
        <div className="navbar__leading-tools">
          <button
            type="button"
            className="navbar__sidebar-toggle"
            onClick={onToggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
          >
            <SidebarPanelIcon
              className="navbar__sidebar-toggle-icon"
              direction={sidebarCollapsed ? 'expand' : 'collapse'}
            />
          </button>
          <NavigationButtons onNavigate={onHistoryNavigate} />
          {sidebarCollapsed && onToggleBrain && (
            <button
              type="button"
              className={`navbar__brain-toggle ${brainOpen ? 'navbar__brain-toggle--active' : ''}`}
              onClick={onToggleBrain}
              title={brainOpen ? 'Close Brain' : 'Open Brain'}
              aria-label={brainOpen ? 'Close Brain' : 'Open Brain'}
              aria-pressed={brainOpen}
            >
              <BrainIcon size={15} />
            </button>
          )}
        </div>
      </div>

      <TabBar
        sprints={sprints}
        onTabClick={onTabClick}
        onNewTab={onNewTab}
        suppressActive={suppressActiveTab}
      />
    </nav>
  );
};

export default Navbar;
